import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { Briefcase, MapPin, GraduationCap, ChevronRight, Loader2, Sparkles, LayoutDashboard, LogOut, Home } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';

const BrowseJobs = () => {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/jobs/all')
      .then(res => {
        setJobs(res.data);
      })
      .catch(err => console.error("❌ Fetch Error:", err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="h-screen w-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-900 via-slate-800 to-indigo-950 flex flex-col items-center justify-center text-indigo-400 gap-4 p-6">
      <Loader2 className="animate-spin" size={40} />
      <p className="font-black uppercase tracking-[0.3em] text-[10px] text-center">{t('scanning_opportunities', 'Scanning for opportunities...')}</p>
    </div>
  );

  return (
    <div className="h-full w-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-900 via-slate-800 to-indigo-950 flex flex-col overflow-hidden relative">

      <header className="h-14 md:h-16 shrink-0 bg-slate-900/80 backdrop-blur-md border-b border-slate-800 flex items-center justify-between px-4 md:px-8 z-50">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/tutor/home')} 
            className="flex items-center gap-2 text-[10px] font-black uppercase text-slate-400 hover:text-indigo-400 transition-all group"
          >
            <LayoutDashboard size={14} className="group-hover:scale-110 transition-transform" />
            <span className="hidden sm:block">{t('dashboard', 'Dashboard')}</span>
          </button>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={() => window.confirm(t('confirm_logout', "Logout?")) && logout()}
            className="flex items-center gap-2 text-[10px] font-black uppercase text-slate-500 hover:text-red-500 transition-all group"
          >
            <span className="hidden sm:block">{t('logout', 'Logout')}</span>
            <LogOut size={14} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto no-scrollbar scroll-smooth">
        <div className="max-w-5xl mx-auto w-full p-4 md:p-10 pb-40">

          <header className="mb-8 md:mb-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-2">
              <h2 className="text-3xl md:text-5xl font-black uppercase italic tracking-tighter text-white leading-none">
                {t('available', 'Available')} <span className="text-indigo-500 text-stroke-white">{t('lessons', 'Lessons')}</span>
              </h2>
              <p className="text-slate-500 font-bold uppercase text-[9px] md:text-[11px] tracking-[0.2em] flex items-center gap-2">
                <Sparkles size={14} className="text-indigo-500 animate-pulse" />
                {t('direct_matches', 'Direct matches for your profile')}
              </p>
            </div>

            <div className="inline-flex self-start md:self-auto bg-slate-900/80 backdrop-blur-md border border-slate-800 px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest text-indigo-400 shadow-xl">
              {jobs.length} {t('requests_found', 'Requests Found')}
            </div>
          </header>

          <div className="grid gap-5 md:gap-8">
            {jobs.length > 0 ? jobs.map((job: any) => (
              <Link
                to={`/tutor/jobs/${job.id}`}
                key={job.id}
                className="group relative bg-slate-900/40 hover:bg-slate-900 border border-slate-800/80 hover:border-indigo-500/50 p-6 md:p-8 rounded-[2.5rem] transition-all flex flex-col md:flex-row md:items-center justify-between gap-6 overflow-hidden shadow-2xl active:scale-[0.97]"
              >
                <div className="hidden lg:block absolute -right-20 -top-20 w-48 h-48 bg-indigo-600/5 blur-[80px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                <div className="space-y-5 relative z-10 flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="bg-indigo-600 text-white px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-indigo-900/40">
                      {job.subject}
                    </span>
                    <div className="w-1 h-1 rounded-full bg-slate-800" />
                    <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest">
                      {t('posted', 'Posted')} {new Date(job.createdAt).toLocaleDateString()}
                    </span>
                  </div>

                  <h3 className="text-xl md:text-2xl font-black text-slate-100 group-hover:text-white transition-colors leading-tight">
                    {job.description.length > 90 ? `${job.description.substring(0, 90)}...` : job.description}
                  </h3>

                  <div className="flex flex-wrap gap-3 md:gap-5">
                    <div className="flex items-center gap-2 bg-slate-950/50 px-4 py-2 rounded-2xl border border-slate-800/50">
                      <MapPin size={14} className="text-indigo-400" />
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{job.location}</span>
                    </div>
                    <div className="flex items-center gap-2 bg-slate-950/50 px-4 py-2 rounded-2xl border border-slate-800/50">
                      <GraduationCap size={14} className="text-indigo-400" />
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{job.gradeLevel}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between md:flex-col md:items-end gap-4 relative z-10 border-t md:border-t-0 border-slate-800/50 pt-6 md:pt-0 shrink-0">
                  <div className="text-left md:text-right">
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-1">{t('budget', 'Budget')}</p>
                    <p className="text-2xl md:text-3xl font-black text-white leading-none">
                      {job.budget} <span className="text-sm text-green-500 ml-1">ETB</span>
                    </p>
                  </div>

                  <div className="flex items-center gap-3 bg-indigo-600 group-hover:bg-indigo-500 text-white px-6 py-3 rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all shadow-xl shadow-indigo-900/30 active:translate-y-1">
                    {t('details', 'Details')} <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            )) : (
              <div className="bg-slate-900/20 border-2 border-dashed border-slate-800 p-16 md:p-32 rounded-[3rem] text-center">
                <Briefcase size={48} className="mx-auto text-slate-800 mb-6" />
                <p className="text-slate-500 font-black uppercase tracking-widest text-xs md:text-sm">{t('no_tutoring_requests', 'No tutoring requests found.')}</p>
                <p className="text-slate-700 text-[10px] mt-3 font-medium uppercase tracking-[0.2em]">{t('check_back_later', 'Check back later for new opportunities')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <button onClick={() => navigate('/tutor/home')} className="fixed bottom-6 right-6 bg-blue-600 hover:bg-blue-500 text-white p-4 rounded-full shadow-2xl shadow-blue-500/30 transition-all hover:-translate-y-1 z-50 group flex items-center gap-0 overflow-hidden">
        <Home size={24} />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:ml-3 transition-all duration-300 ease-in-out whitespace-nowrap font-bold text-sm uppercase tracking-wider">{t('dashboard', 'Dashboard')}</span>
      </button>
    </div>
  );
};

export default BrowseJobs;